import { inject, injectable } from 'inversify';
import i18next from 'i18next';

import type {
  AuthData,
  AuthorizeCode,
  ChangePassword,
  ChangePasswordWithOldPassword,
  CheckCode,
  CheckCodeResponse,
  CustomFormField,
  DeleteAccount,
  ExportAccountData,
  GenerateCode,
  GenerateCodeResponse,
  GetCaptureStatus,
  GetCustomerConsents,
  GetFavorites,
  GetPublisherConsents,
  GetUser,
  GetWatchHistory,
  Login,
  Logout,
  Register,
  ResetPassword,
  SetAuthData,
  UpdateCaptureAnswers,
  UpdateCustomer,
  UpdateCustomerConsents,
  UpdateFavorites,
  UpdateWatchHistory,
} from '../../../../types/account';
import type { Config } from '../../../../types/config';
import AccountService from '../AccountService';
import { ACCESS_MODEL } from '../../../constants';

import VDApiService from './VDApiService';
import type { CreateAccountResponse, GetFavoritesResponse, GetWatchHistoryResponse, LoginResponse, UpdateAccountResponse, User } from './types';

const VD_TERMS_URL = 'https://www.iubenda.com/privacy-policy/50325189';

@injectable()
export default class VDAccountService extends AccountService {
  accessModel = ACCESS_MODEL.AUTHVOD;
  svodOfferIds = [];
  sandbox = false;
  configId: string | undefined;

  constructor(@inject(VDApiService) protected readonly apiService: VDApiService) {
    super({
      canUpdateEmail: false,
      canSupportEmptyFullName: false,
      canChangePasswordWithOldPassword: false,
      canRenewSubscription: false,
      canExportAccountData: false,
      canUpdatePaymentMethod: false,
      canShowReceipts: false,
      canDeleteAccount: false,
      hasNotifications: false,
      watchListSizeLimit: 48,
    });
  }

  protected formatAccount(user: User) {
    return {
      id: user.id,
      uuid: user.id,
      email: user.email,
      fullName: `${user.firstName || ''} ${user.lastName || ''}`,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      metadata: {
        terms: !!user.terms,
      },
      regDate: user.createdAt,
      country: '',
      lastUserIp: '',
    };
  }

  protected getTermsConsent = (): CustomFormField => {
    const termsLink = `<a href="${VD_TERMS_URL}" target="_blank">${i18next.t('account:registration.terms_and_conditions')}</a>`;

    // t('account:registration.terms_consent')
    return {
      type: 'checkbox',
      isCustomRegisterField: true,
      showInProfile: true,
      required: true,
      name: 'terms',
      defaultValue: '',
      label: i18next.t('account:registration.terms_consent', { termsLink }),
      enabledByDefault: false,
      placeholder: '',
      options: {},
      version: '1',
    };
  };

  initialize = async (config: Config, _: string, logoutCallback: () => Promise<void>) => {
    const vdConfig = config.integrations.vd;
    const isSandbox = typeof vdConfig === 'object' && !!vdConfig && 'sandbox' in vdConfig && vdConfig.sandbox === true;
    this.configId = config.id;

    this.apiService.initialize(isSandbox, logoutCallback);
  };

  login: Login = async ({ email, password }) => {
    try {
      const data = await this.apiService.post<LoginResponse>('/customers/login', {
        email,
        password,
      });

      this.apiService.setToken(data.token, '', data.exp);

      const user = this.formatAccount(data.user);
      const customerConsents = await this.getCustomerConsents({ customer: user });

      return {
        isSignedIn: true,
        auth: {
          jwt: data.token,
          refreshToken: '',
        },
        user,
        customerConsents,
      };
    } catch {
      throw new Error('Failed to authenticate user.');
    }
  };

  getAuthData = async () => {
    if (await this.apiService.isAuthenticated()) {
      const credentials = await this.apiService.getToken();

      return {
        jwt: credentials.token,
        refreshToken: credentials.refreshToken,
      } as AuthData;
    }

    return null;
  };

  getUser: GetUser = async () => {
    try {
      const account = await this.apiService.get<LoginResponse>('/customers/me', { withAuthentication: true });
      const user = this.formatAccount(account.user);
      const customerConsents = await this.getCustomerConsents({ customer: user });

      return {
        user,
        customerConsents,
      };
    } catch (error) {
      // failed to get user
      throw new Error('Failed to authenticate user');
    }
  };

  getWatchHistory: GetWatchHistory = async () => {
    try {
      const response = await this.apiService.get<GetWatchHistoryResponse>('/history', { withAuthentication: true });

      return response.docs.map((entry) => ({
        mediaid: entry.mediaId,
        progress: entry.progress,
      }));
    } catch {
      throw new Error('Failed to get the watch history');
    }
  };
  getFavorites: GetFavorites = async () => {
    try {
      const response = await this.apiService.get<GetFavoritesResponse>('/favorites', { withAuthentication: true });

      return response.docs.map((entry) => ({
        mediaid: entry.mediaId,
      }));
    } catch {
      throw new Error('Failed to get favorites');
    }
  };
  updateWatchHistory: UpdateWatchHistory = async ({ user, history }) => {
    try {
      const existingItems = await this.getWatchHistory({ user });
      const existingItemsDict = Object.fromEntries(existingItems.map((item) => [item.mediaid, item.progress]));

      for (let i = 0; i < history.length; i++) {
        const historyItem = history[i];

        if (existingItemsDict[historyItem.mediaid] && existingItemsDict[historyItem.mediaid] === historyItem.progress) {
          continue;
        }

        await this.apiService.post(
          '/history',
          {
            mediaId: historyItem.mediaid,
            progress: historyItem.progress,
          },
          { withAuthentication: true },
        );
      }
    } catch {
      throw new Error('Failed to update watch history');
    }
  };
  updateFavorites: UpdateFavorites = async ({ favorites, user }) => {
    const existingItems = await this.getFavorites({ user });
    const existingItemsIds = existingItems.map((e) => e.mediaid) || [];
    const favoriteIds = favorites.map((e) => e.mediaid);

    // save new favorites
    await Promise.allSettled(
      favoriteIds.map((mediaId) => {
        return !existingItemsIds.includes(mediaId) ? this.apiService.post('/favorites', { mediaId }, { withAuthentication: true }) : Promise.resolve();
      }),
    );

    // delete removed favorites
    await Promise.allSettled(
      existingItemsIds.map((mediaId) => {
        return !favoriteIds.includes(mediaId) ? this.apiService.remove(`/favorites/${mediaId}`, { withAuthentication: true }) : Promise.resolve();
      }),
    );
  };

  updateCustomer: UpdateCustomer = async ({ id, firstName, lastName }) => {
    try {
      const account = await this.apiService.patch<UpdateAccountResponse>(
        `/customers/${id}`,
        {
          firstName,
          lastName,
        },
        { withAuthentication: true },
      );

      return this.formatAccount(account.doc);
    } catch (error) {
      throw new Error('Failed to update user');
    }
  };

  register: Register = async ({ email, password, referrer, consents }) => {
    try {
      await this.apiService.post<CreateAccountResponse>('/customers', {
        email,
        password,
        terms: !!consents.find(({ name }) => name === 'terms')?.value,
      });

      return this.login({ email, password, referrer });
    } catch (error: unknown) {
      throw new Error('Failed to create account.');
    }
  };

  logout: Logout = async () => {
    await this.apiService.removeToken();
  };

  resetPassword: ResetPassword = async ({ customerEmail, resetUrl }) => {
    try {
      await this.apiService.post('/customers/forgot-password', { email: customerEmail, resetUrl });
    } catch (error) {
      throw new Error('Failed to reset your password.');
    }
  };

  changePasswordWithResetToken: ChangePassword = async ({ resetPasswordToken, newPassword }) => {
    try {
      await this.apiService.post('/customers/reset-password', { token: resetPasswordToken, password: newPassword });
    } catch (error) {
      throw new Error('Failed to reset your password.');
    }
  };

  changePasswordWithOldPassword: ChangePasswordWithOldPassword = async () => {
    throw new Error('Method not implemented');
  };

  // consents and capture
  getPublisherConsents: GetPublisherConsents = async () => {
    return [this.getTermsConsent()];
  };

  getCustomerConsents: GetCustomerConsents = async ({ customer }) => {
    return [
      {
        name: 'terms',
        needsUpdate: false,
        state: customer.metadata.terms ? 'accepted' : 'declined',
        value: customer.metadata.terms ? 'true' : 'false',
        version: '1',
      },
    ];
  };

  updateCustomerConsents: UpdateCustomerConsents = async () => {
    // not needed until we add more (and optional) consents
    return [];
  };

  getCaptureStatus: GetCaptureStatus = async ({ customer }) => {
    return {
      isCaptureEnabled: true,
      shouldCaptureBeDisplayed: true,
      settings: [
        {
          answer: {
            firstName: customer.firstName || null,
            lastName: customer.lastName || null,
          },
          enabled: true,
          key: 'firstNameLastName',
          required: true,
        },
      ],
    };
  };

  updateCaptureAnswers: UpdateCaptureAnswers = async ({ customer, ...updatedAnswers }) => {
    return this.updateCustomer({ id: customer.id, ...updatedAnswers });
  };

  // optional features
  loginExternal = undefined;
  exportAccountData?: ExportAccountData | undefined;
  deleteAccount?: DeleteAccount | undefined;
  getExternalLoginProviders = undefined;
  getCustomerPortalUrl = undefined;
  confirmLogin = undefined;
  subscribeToNotifications = async () => false;

  // tv login
  setAuthData: SetAuthData = async ({ authData }) => {
    await this.apiService.setToken(authData.jwt, '');
  };

  generateCode: GenerateCode = async ({ deviceId, deviceName }) => {
    try {
      return await this.apiService.post<GenerateCodeResponse>('/device-flow/generate', { deviceId, deviceName, configId: this.configId });
    } catch (error) {
      throw new Error('Failed to generate code');
    }
  };

  authorizeCode: AuthorizeCode = async ({ code }) => {
    try {
      await this.apiService.post('/device-flow/authorize', { code }, { withAuthentication: true });
    } catch (error) {
      throw new Error('Failed to authorize code');
    }
  };

  checkCode: CheckCode = async ({ code, deviceId }) => {
    try {
      return await this.apiService.get<CheckCodeResponse>('/device-flow/check', { withAuthentication: false }, { code, deviceId });
    } catch (error) {
      throw new Error('Failed to check code');
    }
  };
}
